'use client';

import Image from 'next/image';
import { useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ChevronDown, Check, X, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PricingCard, pricingTiers } from '@/components/marketing/pricing-card';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

const billingCycles = [
  { id: 'monthly', label: 'Monthly', discount: 0 },
  { id: 'quarterly', label: 'Quarterly', discount: 8 },
  { id: 'annual', label: 'Annual', discount: 20 },
];

const comparisonCategories = [
  {
    title: 'Tracking & Maps',
    features: [
      { name: 'Live GPS Tracking', basic: true, safety: true, ai: true, enterprise: true },
      { name: 'Trip History & Replay', basic: true, safety: true, ai: true, enterprise: true },
      { name: 'Street View Integration', basic: false, safety: true, ai: true, enterprise: true },
      { name: 'Route Optimization', basic: false, safety: false, ai: true, enterprise: true },
    ],
  },
  {
    title: 'Camera & Video',
    features: [
      { name: 'Camera Channels', basic: '0', safety: '2', ai: '4', enterprise: 'Unlimited' },
      { name: 'HLS Live Streaming', basic: false, safety: true, ai: true, enterprise: true },
      { name: 'ADAS / DMS Automation', basic: false, safety: false, ai: true, enterprise: true },
      { name: 'Smart Clip Builder', basic: false, safety: false, ai: true, enterprise: true },
    ],
  },
  {
    title: 'AI & Analytics',
    features: [
      { name: 'AI Copilot', basic: false, safety: false, ai: true, enterprise: true },
      { name: 'Anomaly Detection', basic: false, safety: false, ai: true, enterprise: true },
      { name: 'Predictive Maintenance', basic: false, safety: false, ai: true, enterprise: true },
      { name: 'Custom AI Models', basic: false, safety: false, ai: false, enterprise: true },
    ],
  },
  {
    title: 'Safety & Compliance',
    features: [
      { name: 'Driver Safety Scores', basic: false, safety: true, ai: true, enterprise: true },
      { name: 'UBI Risk Scoring', basic: false, safety: false, ai: true, enterprise: true },
      { name: 'Compliance Monitoring', basic: false, safety: true, ai: true, enterprise: true },
      { name: 'Geofencing', basic: 'Radius only', safety: 'Radius + Polygon', ai: 'All types', enterprise: 'All types' },
    ],
  },
  {
    title: 'Reports & Alerts',
    features: [
      { name: 'Report Types', basic: '5', safety: '25+', ai: '75+', enterprise: '100+' },
      { name: 'Scheduled Delivery', basic: false, safety: true, ai: true, enterprise: true },
      { name: 'Multi-Channel Alerts', basic: 'Email only', safety: 'Email + SMS', ai: 'All channels', enterprise: 'All channels' },
      { name: 'Webhook Integrations', basic: false, safety: false, ai: true, enterprise: true },
    ],
  },
  {
    title: 'Administration',
    features: [
      { name: 'User Roles', basic: '2', safety: '5', ai: '8', enterprise: 'Unlimited' },
      { name: 'SSO / SAML', basic: false, safety: false, ai: false, enterprise: true },
      { name: 'API Access', basic: false, safety: 'Read only', ai: 'Full', enterprise: 'Full + Webhooks' },
      { name: 'White-label / CMS', basic: false, safety: false, ai: 'Basic', enterprise: 'Full' },
    ],
  },
];

const faqs = [
  {
    q: 'Can I change my plan later?',
    a: 'Absolutely. You can upgrade or downgrade your plan at any time. When upgrading, you only pay the prorated difference. When downgrading, the credit is applied to your next billing cycle.',
  },
  {
    q: 'What happens after my 14-day free trial?',
    a: 'Your trial includes full access to your selected plan features. After 14 days, you can continue by entering payment details or your account will be paused (not deleted) until you subscribe.',
  },
  {
    q: 'Do you offer custom pricing for large fleets?',
    a: 'Yes. For fleets with 200+ vehicles, we offer custom Enterprise pricing with dedicated support, custom integrations, and volume discounts. Contact our sales team for a tailored quote.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept credit/debit cards via Stripe, PayPal, PayFast (South Africa), Flutterwave (Africa), and M-Pesa (East Africa). Bank transfers are available for annual Enterprise plans.',
  },
  {
    q: 'Is there a setup fee?',
    a: 'Basic and Safety plans have no setup fee. AI-Powered plans include a $199 one-time setup fee (waived on annual billing). Enterprise setup fees are negotiable based on scope.',
  },
  {
    q: 'How is per-vehicle and per-device pricing calculated?',
    a: 'Each plan includes a base allocation of vehicles and devices. Additional units are billed at the per-vehicle ($5-$15/mo) and per-device ($3-$8/mo) rate for your plan tier.',
  },
];

function CellValue({ value }: { value: boolean | string }) {
  if (typeof value === 'boolean') {
    return value ? (
      <Check className="mx-auto h-4 w-4 text-neon-cyan" />
    ) : (
      <X className="mx-auto h-4 w-4 text-slate-300" />
    );
  }
  return <span className="text-sm text-slate-300">{value}</span>;
}

export default function PricingPage() {
  const [cycle, setCycle] = useState('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openCategory, setOpenCategory] = useState<number>(0);
  const currentCycle = billingCycles.find((c) => c.id === cycle)!;

  return (
    <>
      {/* ── Hero ─────────────────────────────────────── */}
      <section className="gradient-hero relative overflow-hidden py-20 lg:py-28">
        <div className="absolute -top-40 -right-40 h-[400px] w-[400px] rounded-full bg-neon-cyan/10 blur-[100px]" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-8">
          <Badge variant="accent" className="mb-4">Transparent Pricing</Badge>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Plans that <span className="text-gradient">scale with your fleet</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            Start with a 14-day free trial. No credit card required. Upgrade, downgrade, or cancel anytime.
          </p>

          <div className="mx-auto mt-8 max-w-4xl">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={PAGE_HERO_IMAGES.pricing.src}
                alt={PAGE_HERO_IMAGES.pricing.alt}
                width={1600}
                height={900}
                className="h-auto w-full rounded-2xl object-cover"
                priority
              />
            </div>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-white/10 glass-card p-1">
            {billingCycles.map((c) => (
              <button
                key={c.id}
                onClick={() => setCycle(c.id)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
                  cycle === c.id
                    ? 'bg-gradient-to-r from-neon-cyan to-neon-purple text-white shadow-md'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                {c.label}
                {c.discount > 0 && (
                  <span className={`ml-1.5 text-xs ${cycle === c.id ? 'text-cyan-200' : 'text-neon-cyan'}`}>
                    -{c.discount}%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Cards ────────────────────────────── */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {pricingTiers.map((tier, i) => (
              <PricingCard
                key={tier.tier}
                tier={tier}
                index={i}
                billingCycle={cycle}
                discount={currentCycle.discount}
              />
            ))}
          </div>

          {/* Add-on pricing */}
          <div className="mt-12 rounded-2xl border border-white/10 bg-white/5 p-8">
            <h3 className="mb-4 text-lg font-semibold text-white">Add-on Pricing</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: 'Additional Vehicle', range: '$5 - $15 / mo' },
                { label: 'Additional Device', range: '$3 - $8 / mo' },
                { label: 'Additional Seat', range: '$10 - $25 / mo' },
                { label: 'SIM Card Data', range: 'From $2 / mo' },
              ].map((addon) => (
                <div key={addon.label} className="rounded-xl glass-card p-4 border border-white/10">
                  <p className="text-sm font-medium text-white">{addon.label}</p>
                  <p className="text-sm text-slate-300">{addon.range}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature Comparison ───────────────────────── */}
      <section className="border-t border-white/8 bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="mb-8 text-center text-2xl font-bold text-white">
            Detailed Feature Comparison
          </h2>

          <div className="space-y-3">
            {comparisonCategories.map((cat, catIdx) => (
              <div key={cat.title} className="rounded-2xl border border-white/10 glass-card overflow-hidden">
                <button
                  onClick={() => setOpenCategory(openCategory === catIdx ? -1 : catIdx)}
                  className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-white/5 transition"
                >
                  <span className="font-semibold text-white">{cat.title}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-slate-300 transition-transform ${openCategory === catIdx ? 'rotate-180' : ''}`}
                  />
                </button>
                {openCategory === catIdx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <table className="w-full">
                      <thead>
                        <tr className="border-t border-white/8 bg-white/5">
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-300">Feature</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-300">Basic</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-300">Safety</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-neon-cyan bg-neon-cyan/10">AI-Powered</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-300">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cat.features.map((feat) => (
                          <tr key={feat.name} className="border-t border-white/5">
                            <td className="px-6 py-3 text-sm text-slate-300">{feat.name}</td>
                            <td className="px-4 py-3 text-center"><CellValue value={feat.basic} /></td>
                            <td className="px-4 py-3 text-center"><CellValue value={feat.safety} /></td>
                            <td className="px-4 py-3 text-center bg-neon-cyan/5"><CellValue value={feat.ai} /></td>
                            <td className="px-4 py-3 text-center"><CellValue value={feat.enterprise} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────── */}
      <section className="border-t border-white/8 bg-white/5 py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="mb-10 text-center">
            <Badge variant="accent" className="mb-4">
              <HelpCircle className="h-3 w-3" /> FAQ
            </Badge>
            <h2 className="text-2xl font-bold text-white">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="rounded-2xl border border-white/10 glass-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-white/5 transition"
                >
                  <span className="pr-4 font-medium text-white">{faq.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-slate-300 transition-transform ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-white/8"
                  >
                    <p className="px-6 py-4 text-sm leading-relaxed text-slate-300">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────── */}
      <section className="gradient-hero-vivid py-16">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <h2 className="text-3xl font-bold text-white">Still have questions?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-slate-300">
            Our team is ready to help you find the perfect plan for your fleet.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Link href="/signup">
              <Button size="lg" className="rounded-full btn-neon text-white">
                Start Free Trial
              </Button>
            </Link>
            <Link href="/about#contact">
              <Button size="lg" variant="ghost" className="rounded-full border border-white/30 text-white hover:bg-white/8">
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
